import React, { useCallback, useRef, useState } from "react"
import XGSSelect, { XGSSelectOption } from "../../../xgs-select/xgsSelect";
import {
  isSelectBlankValue,
  SELECT_EMPTY_FILTER_VALUE,
} from "./selectColumnFilterUtils";

export interface SelectColumnFilterProps {
    column: any
}

interface SelectFilterOption extends XGSSelectOption {
  originalKey: unknown;
}

const defaultSortFn = (item1: any, item2: any) => item1 > item2 ? 1 : -1;

/** Sub-pixel rounding of the control position must not be treated as scrolling. */
const ANCHOR_DRIFT_TOLERANCE_PX = 1;

export const SelectColumnFilter: React.FC<SelectColumnFilterProps> = ({
    column
}) => {
  const { filterLabelFormatter, filterSortFn } = column.columnDef.meta ?? {};

  const [value, setValue] = useState<XGSSelectOption | undefined>(() => {
    const fv = column.getFilterValue();
    if (fv === SELECT_EMPTY_FILTER_VALUE) {
      return { value: SELECT_EMPTY_FILTER_VALUE, label: "—" };
    }
    return fv != null && fv !== ""
      ? { value: String(fv), label: filterLabelFormatter ? filterLabelFormatter(fv) : String(fv) }
      : undefined;
  });

  const containerRef = useRef<HTMLDivElement | null>(null);
  const anchorPositionRef = useRef<{ left: number; top: number } | null>(null);

  const getControlPosition = () => {
    if (!containerRef.current) return null;
    const { left, top } = containerRef.current.getBoundingClientRect();
    return { left, top };
  };

  const onMenuOpen = useCallback(() => {
    anchorPositionRef.current = getControlPosition();
  }, []);

  const onMenuClose = useCallback(() => {
    anchorPositionRef.current = null;
  }, []);

  const closeMenuOnScroll = useCallback((e: Event) => {
    const target = e.target as HTMLElement | Document | null;
    // Keep open while scrolling the options list itself
    if (target instanceof HTMLElement && target.closest(".xgs-select__menu")) {
      return false;
    }
    // The menu is portaled and fixed-positioned, so it detaches from the input
    // as soon as scrolling (e.g. horizontally through the columns) moves the control
    const anchorPosition = anchorPositionRef.current;
    const currentPosition = getControlPosition();
    if (
      anchorPosition &&
      currentPosition &&
      (Math.abs(currentPosition.left - anchorPosition.left) > ANCHOR_DRIFT_TOLERANCE_PX ||
        Math.abs(currentPosition.top - anchorPosition.top) > ANCHOR_DRIFT_TOLERANCE_PX)
    ) {
      return true;
    }
    // Keep open when the datatable scrolls without moving the header (e.g. vertical row scroll)
    if (target instanceof HTMLElement && target.closest(".xgs-data-table__wrapper")) {
      return false;
    }
    return true;
  }, []);

  const options: SelectFilterOption[] = [];
  let emptyCount = 0;

  [...column.getFacetedUniqueValues().entries()]
    .sort(filterSortFn || defaultSortFn)
    .forEach(([key, count]: [unknown, number]) => {
      if (isSelectBlankValue(key)) {
        emptyCount += count;
        return;
      }
      const optionValue = String(key);
      options.push({
        value: optionValue,
        originalKey: key,
        label: `${filterLabelFormatter ? filterLabelFormatter(key) : optionValue} (${count})`,
      });
    });

  if (emptyCount > 0) {
    options.unshift({
      value: SELECT_EMPTY_FILTER_VALUE,
      originalKey: SELECT_EMPTY_FILTER_VALUE,
      label: `— (${emptyCount})`,
    });
  }

  return (
    <div ref={containerRef}>
      <XGSSelect
        id='column-filter-select'
        value={value}
        options={options}
        disabled={false}
        onValueChange={(opt) => {
          if (!opt) {
            column.setFilterValue(undefined);
            setValue(undefined);
            return;
          }
          const selected = options.find((option) => option.value === opt.value);
          column.setFilterValue(selected ? selected.originalKey : undefined);
          setValue(opt);
        }}
        clearable
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 1100 }),
        }}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        closeMenuOnScroll={closeMenuOnScroll}
      />
    </div>
  );
};
